// Helpers puros y constantes compartidas por los componentes de Contabilidad Siigo.

export const TRANSACTION_TYPE_LABELS = {
  comprobante_originacion: "Originación de crédito",
  comprobante_inverso_originacion: "Reversión de originación",
  comprobante_originacion_consolidado: "Originación de crédito (consolidado)",
  facturacion_comisiones: "Factura de comisión",
  nota_credito_comisiones: "Nota crédito de comisión",
  facturacion_intereses: "Factura de intereses",
  nota_credito_intereses: "Nota crédito de intereses",
  comprobante_recaudo_quota: "Recaudo de cuota",
  comprobante_inverso_recaudo_quota: "Reversión de recaudo (cuota)",
  comprobante_recaudo_payment: "Recaudo de pago",
  comprobante_inverso_recaudo_payment: "Reversión de recaudo (pago)",
  comprobante_recaudo_quota_consolidado: "Recaudo de cuotas (consolidado)",
  comprobante_recaudo_payment_consolidado: "Recaudo de pagos (consolidado)",
  comprobante_recaudo_sobrante: "Recaudo — sobrante",
  comprobante_inverso_recaudo_sobrante: "Reversión de recaudo — sobrante",
  comprobante_recaudo_no_identificado: "Recaudo — no identificado",
  comprobante_inverso_recaudo_no_identificado: "Reversión de recaudo — no identificado",
};

export const friendlyTransactionType = (transactionType) =>
  TRANSACTION_TYPE_LABELS[transactionType] || transactionType || "Tipo desconocido";

const currencyFormatter = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  maximumFractionDigits: 0,
});

export const formatCurrency = (value) =>
  typeof value === "number" && !Number.isNaN(value) ? currencyFormatter.format(value) : "—";

export const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("es-CO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

/** Recorta y traduce mensajes técnicos comunes para que no asusten a un usuario no técnico. */
export const friendlyErrorMessage = (errorDetail) => {
  if (!errorDetail) return "No se registró un detalle del error.";
  if (errorDetail.length > 220) return `${errorDetail.slice(0, 220)}…`;
  return errorDetail;
};

export const getToday = () => new Date().toISOString().split("T")[0];

export const getFirstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

export const buildQueryString = (params) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.append(key, String(value));
  });
  return query.toString();
};

// ── Manejo amigable de errores de Siigo ────────────────────────────────────

/** Traducciones legibles para los códigos de error que devuelve Siigo. */
const SIIGO_ERROR_CODE_LABELS = {
  invalid_reference: "Referencia inválida",
  invalid_value: "Valor inválido",
  required: "Campo obligatorio",
  parameter_required: "Parámetro obligatorio",
  already_exists: "El registro ya existe",
  not_found: "No encontrado",
  unauthorized: "No autorizado",
  forbidden: "Sin permisos",
  duplicated: "Registro duplicado",
};

export const siigoErrorCodeLabel = (code) => SIIGO_ERROR_CODE_LABELS[code] || code || "Error";

/** Traduce a español los mensajes técnicos más comunes de Siigo; si no reconoce el
 *  patrón, devuelve el mensaje original tal cual. */
export const humanizeSiigoMessage = (message) => {
  if (!message) return "Ocurrió un error no especificado.";
  const customer = message.match(/customer doesn't exist:\s*([^\s".]+)/i);
  if (customer) return `El cliente no existe en Siigo (identificación: ${customer[1]}).`;
  const product = message.match(/product doesn't exist:\s*([^\s".]+)/i);
  if (product) return `El producto no existe en Siigo (código: ${product[1]}).`;
  if (/is required/i.test(message)) return `Falta un dato obligatorio: ${message}.`;
  return message;
};

/** Extrae el primer objeto JSON balanceado ({ ... }) que aparezca en el texto,
 *  ignorando llaves que estén dentro de cadenas. Devuelve el substring o null. */
const extractJsonObject = (text) => {
  const anchor = text.indexOf('{"Status"');
  const start = anchor !== -1 ? anchor : text.indexOf("{");
  if (start === -1) return null;

  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (escaped) {
      escaped = false;
    } else if (ch === "\\") {
      escaped = true;
    } else if (ch === '"') {
      inString = !inString;
    } else if (!inString) {
      if (ch === "{") depth++;
      else if (ch === "}") {
        depth--;
        if (depth === 0) return text.slice(start, i + 1);
      }
    }
  }
  return null;
};

/**
 * Convierte el string crudo de error de Siigo en una estructura consultable.
 * Ejemplo de entrada:
 *   [400 Bad Request] during [POST] to [https://api.siigo.com/v1/journals] [..]: [{"Status":400,"Errors":[{"Code":"invalid_reference","Message":"..."}]}]
 */
export const parseSiigoError = (rawError) => {
  const raw =
    typeof rawError === "string" ? rawError : rawError == null ? "" : JSON.stringify(rawError);

  const result = { raw, httpStatus: null, method: null, url: null, status: null, errors: [] };
  if (!raw) return result;

  const httpMatch = raw.match(/^\s*\[(\d{3}[^\]]*)\]/);
  if (httpMatch) result.httpStatus = httpMatch[1].trim();
  const methodMatch = raw.match(/during\s*\[([A-Za-z]+)\]/);
  if (methodMatch) result.method = methodMatch[1].toUpperCase();
  const urlMatch = raw.match(/to\s*\[(https?:\/\/[^\]]+)\]/);
  if (urlMatch) result.url = urlMatch[1].trim();

  const jsonText = extractJsonObject(raw);
  if (jsonText) {
    try {
      const parsed = JSON.parse(jsonText);
      if (parsed.Status != null) result.status = parsed.Status;
      const list = Array.isArray(parsed.Errors) ? parsed.Errors : [];
      result.errors = list.map((e) => ({
        code: e.Code || null,
        message: e.Message || null,
        params: Array.isArray(e.Params) ? e.Params : e.Params ? [e.Params] : [],
        detail: e.Detail || null,
      }));
    } catch {
      /* Se ignora: se mostrará el texto crudo como respaldo. */
    }
  }
  return result;
};

/** Mensaje corto y legible para mostrar en línea (ej. una celda de tabla). */
export const summarizeSiigoError = (rawError) => {
  const { errors } = parseSiigoError(rawError);
  if (errors.length > 0 && errors[0].message) return humanizeSiigoMessage(errors[0].message);
  return friendlyErrorMessage(typeof rawError === "string" ? rawError : "");
};

/** Formatea (indenta) un payload JSON crudo para el detalle técnico; si no es JSON
 *  válido, devuelve el texto original o un guion. */
export const prettyPrintPayload = (payloadRaw) => {
  try {
    return JSON.stringify(JSON.parse(payloadRaw || "{}"), null, 2);
  } catch {
    return payloadRaw || "—";
  }
};
